import React, { useState } from 'react'
import styled from 'styled-components'
import copy from 'copy-to-clipboard'
import { useRouteMatch } from 'react-router-dom'
import { useToast } from '@sketch/toasts'
import {
  Box,
  Input,
  TextArea,
  Checkbox,
  Form,
  PasswordInput,
  generatePassword,
  Flex,
  FlatButton,
} from '@sketch/components'
import { LabelStyled } from './PartialShareModal.styles'
import { routes } from '@sketch/modules-common'
import { usePreviewForm } from './usePreviewForm'
import { PreviewLinkSection } from './PreviewLinkSection'
import { DatePicker } from '../../../activity/components/Notifications/NotificationSearch/NotificationSearch.styles'
import { DocumentInfoHeader } from './DocumentInfoHeader'

const DatePickerStyled = styled(DatePicker)`
  font-size: ${({ theme }) => theme.fontSizes.E};
  width: 150px;
`

const PasswordToggle = styled(FlatButton)`
  padding: 0 12px;
  margin-top: 1px; /* stylelint-disable-line scales/space */
  height: 22px;
  font-size: ${({ theme }) => theme.fontSizes.D};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  text-decoration: underline;
`

const Header = styled.p`
  /* has to be important because paragraphs have a default padding bottom of 20  */
  padding-bottom: 24px !important; /* stylelint-disable-line declaration-no-important */
`

const OptionsHeader = styled(LabelStyled)``

const PreviewPasswordInput = styled(PasswordInput)`
  /* Override the right padding to leave space for the three accessory buttons
    in the Preview Password input (Generate, Copy, and Show Password).
    Each button is 48px wide, so we need 3 * 48px = 144px total. */
  /* stylelint-disable-next-line declaration-no-important */
  padding-right: calc(48px * 3) !important;
`

const DescriptionTextArea = styled(TextArea)`
  min-height: 40px; /* match other inputs' height */
  max-height: 128px; /* enough to fit the 255 char limit */
  resize: vertical;
`

interface SettingsTabProps {
  shareName: string
  shareThumbnail?: string
  shareIdentifier: string
  isEditMode?: boolean
  reopenPartialSharingModal?: (identifier?: string) => void
}

export const SettingsTab = ({
  shareName,
  shareThumbnail,
  shareIdentifier,
  isEditMode = false,
  reopenPartialSharingModal,
}: SettingsTabProps) => {
  const [showPasswordInput, setShowPasswordInput] = useState(!isEditMode)
  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    initialValues,
  } = usePreviewForm()
  const { showToast } = useToast()

  const handleOnCopy = () => {
    copy(values.password)
    showToast('Password copied')
  }

  const handleOnGeneratePassword = () => {
    setFieldValue('password', generatePassword(12))
  }

  const isInWorkspaceSettings = useRouteMatch(
    routes.WORKSPACE_SETTINGS_DOCUMENT_PREVIEWS.template()
  )

  return (
    <>
      {isInWorkspaceSettings && (
        <DocumentInfoHeader
          name={shareName}
          identifier={shareIdentifier}
          thumbnail={shareThumbnail}
        />
      )}
      {isEditMode ? (
        <PreviewLinkSection
          reopenPartialSharingModal={reopenPartialSharingModal}
        />
      ) : (
        <Header>
          Create a preview to share only specific pages and prototypes of{' '}
          <strong>&quot;{shareName}&quot;</strong>.
        </Header>
      )}
      <Form.Field
        name="previewName"
        label="Preview Name"
        errorText={touched.previewName ? errors.previewName : undefined}
      >
        <Input
          type="text"
          name="previewName"
          placeholder="Give your preview a name"
          value={values.previewName}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Form.Field>

      <Form.Field name="description" label="Description (Optional)">
        <DescriptionTextArea
          name="description"
          placeholder="A quick summary of your preview’s contents"
          value={values.description}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={255}
        />
      </Form.Field>
      <Box mb={2}>
        <OptionsHeader>Preview Options</OptionsHeader>
        <Box mb={2}>
          <Checkbox
            name="inspectEnabled"
            checked={values.inspectEnabled}
            onChange={handleChange}
          >
            Allow inspecting and asset downloads
          </Checkbox>
        </Box>
        <Box mb={2}>
          <Checkbox
            name="commentsEnabled"
            checked={values.commentsEnabled}
            onChange={handleChange}
          >
            Allow commenting with Sketch account
          </Checkbox>
        </Box>
        <Box mb={2}>
          <Flex alignItems="center">
            <Checkbox
              name="passwordProtected"
              checked={values.passwordProtected}
              onChange={e => {
                if (!isEditMode) {
                  handleChange(e)
                  return
                }

                // when the preview previously wasn't password protected
                if (!initialValues.passwordProtected) {
                  setShowPasswordInput(!showPasswordInput)
                  handleChange(e)
                  return
                }

                if (showPasswordInput) {
                  setShowPasswordInput(false)
                  setFieldValue('password', '')
                  setFieldTouched('password', false)
                }

                handleChange(e)
              }}
            >
              Protect with password
            </Checkbox>
            {values.passwordProtected && !showPasswordInput && (
              <PasswordToggle onClick={() => setShowPasswordInput(true)}>
                Create New Password
              </PasswordToggle>
            )}
          </Flex>
          {showPasswordInput && values.passwordProtected && (
            <Box mt={1} ml={6}>
              <Form.Field
                name="password"
                errorText={touched.password ? errors.password : undefined}
              >
                <PreviewPasswordInput
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onPwdCopy={handleOnCopy}
                  onGeneratePassword={handleOnGeneratePassword}
                />
              </Form.Field>
            </Box>
          )}
        </Box>
      </Box>
      <Box mb={2}>
        <Checkbox
          name="expiryDateEnabled"
          checked={values.expiryDateEnabled}
          onChange={handleChange}
        >
          Expire preview link on…
        </Checkbox>
        {values.expiryDateEnabled && (
          <Box mt={1} ml={6}>
            <Form.Field
              name="expiryDate"
              errorText={touched.expiryDate ? errors.expiryDate : undefined}
            >
              <DatePickerStyled
                name="expiryDate"
                type="date"
                placeholder="Expire on"
                aria-label="Expire on"
                value={values.expiryDate}
                isDarkMode={false}
                onChange={handleChange}
                onBlur={handleBlur}
                min={
                  //adds 86400000 seconds = 24 hours so we never include today
                  new Date(Date.now() + 86400000).toISOString().split('T')[0]
                }
              />
            </Form.Field>
          </Box>
        )}
      </Box>
    </>
  )
}
